import { create } from 'zustand';
// ── Pending logout (MEDIUM fix, adversarial review) ─────────────────────────
//
// A marker — no token, no full session data — so ADR-010's "no refresh
// token in client-readable storage" stays untouched. `localStorage` (not
// `sessionStorage`) so the intent survives a reload too: closing the tab
// mid-offline-signOut must not accidentally resurrect the old session on the
// NEXT launch before this flag gets a chance to flush.
//
// HIGH fix (use-auth.ts finding #1) — the flag now carries the `sid` of the
// session it targets (`null` when unknown, e.g. signOut() from an already-
// empty store). Lives here (not use-auth.ts, where it originally lived)
// because `setSession`/`authStore.set` — the ONLY two places a session is
// ever installed — must clear it the instant a NEW session lands, and
// use-auth.ts already depends on this module (the reverse import would be
// circular).
const PENDING_LOGOUT_STORAGE_KEY = 'jq4_pending_logout';
function hasLocalStorage() {
    try {
        return typeof localStorage !== 'undefined';
    }
    catch {
        return false;
    }
}
export function markPendingLogout(sid) {
    if (!hasLocalStorage())
        return;
    try {
        const target = { sid };
        localStorage.setItem(PENDING_LOGOUT_STORAGE_KEY, JSON.stringify(target));
    }
    catch {
        // Best-effort (e.g. a private-browsing quota rejection) — the next
        // signOut/refresh attempt tries the network call again regardless.
    }
}
export function clearPendingLogout() {
    if (!hasLocalStorage())
        return;
    try {
        localStorage.removeItem(PENDING_LOGOUT_STORAGE_KEY);
    }
    catch {
        // ignore
    }
}
/** Full parsed target (sid it was armed for), or `null` if no flag is set. */
export function getPendingLogoutTarget() {
    if (!hasLocalStorage())
        return null;
    try {
        const raw = localStorage.getItem(PENDING_LOGOUT_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && 'sid' in parsed) {
            const sid = parsed.sid;
            return { sid: typeof sid === 'string' ? sid : null };
        }
        // Legacy boolean-only flag ('1') from before this fix, or any other
        // unrecognized shape — treat as "pending, unknown target" so an
        // in-flight upgrade never silently drops a real intent.
        return { sid: null };
    }
    catch {
        return null;
    }
}
/** Exported for direct unit testing and for callers that need to introspect it. */
export function hasPendingLogout() {
    return getPendingLogoutTarget() !== null;
}
export const useAuthStore = create((set) => ({
    session: null,
    mfaIntent: null,
    sessionExpired: false,
    sessionExpiredReason: null,
    setSession: (session) => {
        // HIGH fix (adversarial review, use-auth.ts finding #1) — ANY successful
        // login/session install (this is the ONE action every LoginPage across
        // both apps calls, via useAuth().setSession) must drop a stale
        // pending-logout flag. Without this, "sign out offline (flag set) → sign
        // in again before the flag flushes" left the flag armed, and the NEXT
        // refresh revoked the brand-new session instead of the one that was
        // actually signed out of. See `performRefresh` in use-auth.ts for the
        // second half of this fix (the sid-target check, for the same race when
        // this clear itself loses — e.g. a flush already in flight elsewhere).
        if (session)
            clearPendingLogout();
        set({ session, mfaIntent: null, sessionExpired: false, sessionExpiredReason: null });
    },
    setMfaIntent: (intent) => set({ mfaIntent: intent }),
    expire: (reason) => set((state) => ({
        session: null,
        mfaIntent: null,
        // A suspension must be reported even when there was no in-memory session
        // (cold bootstrap after reload): the user still deserves to know why.
        sessionExpired: state.session !== null || state.sessionExpired || reason !== undefined,
        sessionExpiredReason: reason ?? null,
    })),
    clearSessionExpired: () => set({ sessionExpired: false, sessionExpiredReason: null }),
    signOut: () => set({ session: null, mfaIntent: null, sessionExpired: false, sessionExpiredReason: null }),
}));
// Helpers para callers no-React (interceptor en client.ts, módulos con
// efectos colaterales, tests). Bajo el capó usan el mismo store de Zustand.
export const authStore = {
    get: () => useAuthStore.getState().session,
    set: (session) => {
        // Same clear as the React `setSession` action above (HIGH fix,
        // use-auth.ts finding #1) — this is the non-React path `performRefresh`
        // uses to install a successfully-refreshed/rotated session.
        if (session)
            clearPendingLogout();
        useAuthStore.setState({
            session,
            mfaIntent: null,
            sessionExpired: false,
            sessionExpiredReason: null,
        });
    },
    getAccessToken: () => useAuthStore.getState().session?.accessToken ?? null,
    getRefreshToken: () => useAuthStore.getState().session?.refreshToken ?? null,
    expire: (reason) => useAuthStore.getState().expire(reason),
    signOut: () => useAuthStore.getState().signOut(),
    clearSessionExpired: () => useAuthStore.getState().clearSessionExpired(),
    subscribe: (fn) => useAuthStore.subscribe((state) => fn(state.session)),
};
function decodeJwt(token) {
    try {
        const [, payload] = token.split('.');
        if (!payload)
            return null;
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json);
    }
    catch {
        return null;
    }
}
export function sessionFromTokens(input) {
    const claims = decodeJwt(input.accessToken);
    if (!claims)
        return null;
    return {
        accessToken: input.accessToken,
        refreshToken: input.refreshToken,
        expiresAt: Date.now() + input.expiresIn * 1000,
        user: {
            id: claims.sub,
            role: claims.role,
            organizationId: claims.org_id,
            email: input.email ?? claims.email ?? null,
        },
        sid: claims.sid ?? null,
    };
}
//# sourceMappingURL=auth-store.js.map